import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { RememberMeStorageService } from './remember-me-storage.service';

@Injectable({
  providedIn: 'root'
})
export class DeviceFingerprintService {
  private fpPromise: Promise<any>;
  private cachedFingerprint: string | null = null;

  constructor(private rememberMeStorage: RememberMeStorageService) {
    this.fpPromise = FingerprintJS.load();
  }

  /**
   * Returns a stable per-browser identifier: reused from storage once computed,
   * so it survives across sessions instead of relying on FingerprintJS producing
   * the exact same visitorId on every login (it isn't guaranteed to).
   */
  async getFingerprint(): Promise<string> {
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }

    const stored = this.rememberMeStorage.getDeviceFingerprint();
    if (stored) {
      this.cachedFingerprint = stored;
      return stored;
    }

    let fingerprint: string;
    try {
      const fp = await this.fpPromise;
      const result = await fp.get();
      fingerprint = result.visitorId;
    } catch (error) {
      console.error('Error generating device fingerprint:', error);
      fingerprint = this.generateFallbackFingerprint();
    }

    this.cachedFingerprint = fingerprint;
    this.rememberMeStorage.setDeviceFingerprint(fingerprint);

    return fingerprint;
  }

  private generateFallbackFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('fingerprint', 2, 2);
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');

    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    return 'fallback_' + Math.abs(hash).toString(36);
  }

  clearCache(): void {
    this.cachedFingerprint = null;
  }
}
